import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from './api';
import { generateRequestId, encryptAES } from '@/utils/crypto';

export interface UserProfile {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  full_name?: string;
  mobile_number?: string;
  status: string;
  profile_image?: string;
  employee_id?: string;
  created_at?: string;
  is_password_exist?: boolean;
  groups: Array<{
    group_key: string;
    group_name: string;
    roles: string[];
  }>;
}

export interface MeApiResponse {
  user: UserProfile;
  permissions: string[];
  tenant_key: string;
  last_login: string;
  registration_type: string;
  assigned_course_count: number;
  completed_course_count: number;
}

export interface UpdateProfileRequest {
  given_name?: string;
  family_name?: string;
  email?: string;
  mobile_number?: string | null;
  profile_image?: string; // Changed from profile_image to match API
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  code: number;
  description?: string;
  error?: {
    error_code: string;
    error_description: string;
  };
}

export interface ProfileResponse {
  success: boolean;
  code: number;
  description?: string;
  data?: MeApiResponse;
  error?: {
    error_code: string;
    error_description: string;
    meta_data?: any;
  };
}

class ProfileService {
  /**
   * Get current user's profile using /me endpoint
   */
  async getMyProfile(): Promise<MeApiResponse> {
    try {
      const response = await apiClient.get<ProfileResponse>(API_ENDPOINTS.AUTH.ME);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error?.error_description || response.data.description || 'Failed to fetch profile');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Update current user's profile
   */
  async updateMyProfile(updateData: UpdateProfileRequest): Promise<MeApiResponse> {
    try {
      const response = await apiClient.patch<ProfileResponse>(API_ENDPOINTS.AUTH.ME, updateData);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      // Handle specific error cases from API documentation
      const errorCode = response.data.error?.error_code;
      const errorMessage = response.data.error?.error_description || response.data.description || 'Failed to update profile';
      
      if (errorCode === 'EMAIL_ALREADY_EXISTS') {
        throw new Error('This email address is already in use by another account.');
      } else if (errorCode === 'VALIDATION_ERROR') {
        const field = response.data.error?.meta_data?.field;
        const value = response.data.error?.meta_data?.value;
        if (field === 'email') {
          throw new Error('Please enter a valid email address.');
        }
        throw new Error(`Invalid ${field}: ${value}`);
      } else if (errorCode === 'AUTH_001') {
        throw new Error('You are not authorized to perform this action. Please login again.');
      }
      
      throw new Error(errorMessage);
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      
      // Handle network errors
      if (error.response?.status === 409) {
        throw new Error('This email address is already in use by another account.');
      } else if (error.response?.status === 400) {
        throw new Error('Please check your input and try again.');
      } else if (error.response?.status === 401) {
        throw new Error('You are not authorized to perform this action. Please login again.');
      }
      
      throw error;
    }
  }

  /**
   * Change user's password with old password verification
   */
  async changePassword(userId: string, data: ChangePasswordRequest): Promise<any> {
    try {
      // Generate request_id for encryption
      const requestId = generateRequestId();

      // Encrypt old and new passwords
      const encryptedOldPassword = encryptAES(data.old_password, requestId);
      const encryptedNewPassword = encryptAES(data.new_password, requestId);

      const response = await apiClient.post(
        API_ENDPOINTS.USERS.CHANGE_PASSWORD(userId),
        {
          old_password: encryptedOldPassword,
          new_password: encryptedNewPassword,
          request_id: requestId
        }
      );

      if (response.data.success) {
        return response.data;
      }

      const errorMessage = response.data.error?.error_description || response.data.description || 'Failed to change password';
      throw new Error(errorMessage);
    } catch (error: any) {
      console.error('Error changing password:', error);

      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Current password is incorrect');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error?.error_description || 'Invalid new password or same as current password');
      }
      if (error.response?.data?.error?.error_description) {
        throw new Error(error.response.data.error.error_description);
      }
      throw error;
    }
  }

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.put(API_ENDPOINTS.STORAGE.UPLOAD, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data.url;
      }
      
      throw new Error('Failed to upload profile picture');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  }

  /**
   * Update profile picture
   */
  async updateProfilePicture(profileImageUrl: string): Promise<MeApiResponse> {
    return this.updateMyProfile({ profile_image: profileImageUrl });
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate mobile number format
   */
  validateMobileNumber(mobile: string): boolean {
    // Basic international phone number validation
    const mobileRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return mobileRegex.test(mobile.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Get profile field validation rules
   */
  getValidationRules(): {
    given_name: { required: boolean; minLength: number; maxLength: number };
    family_name: { required: boolean; minLength: number; maxLength: number };
    email: { required: boolean; pattern: string };
    mobile_number: { required: boolean; pattern: string };
  } {
    return {
      given_name: { required: true, minLength: 1, maxLength: 50 },
      family_name: { required: true, minLength: 1, maxLength: 50 },
      email: { required: true, pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$' },
      mobile_number: { required: false, pattern: '^[\\+]?[1-9][\\d]{0,15}$' }
    };
  }
}

export const profileService = new ProfileService();